export const getCardStep = ({ firstOffset, secondOffset }) => Math.abs(secondOffset - firstOffset);

export const clampTarget = (target, maximum) => Math.min(Math.max(target, 0), Math.max(maximum, 0));

export const shouldSuppressClick = (distance) => Math.abs(distance) > 8;

export const updateMaxDragDistance = (previous, delta) => Math.max(previous, Math.abs(delta));

const getDirection = (element) => {
  const computedDirection = globalThis.getComputedStyle?.(element).direction;
  return computedDirection || element.closest?.('[dir]')?.getAttribute('dir') || element.ownerDocument?.documentElement?.dir || 'ltr';
};

let rtlScrollType;
const rtlBoundaryTolerance = 1;

const getRtlScrollType = (documentRef = globalThis.document) => {
  if (rtlScrollType || !documentRef?.body) return rtlScrollType ?? 'negative';

  const probe = documentRef.createElement('div');
  const child = documentRef.createElement('div');
  probe.dir = 'rtl';
  probe.style.cssText = 'width:4px;height:1px;overflow:scroll;position:absolute;top:-9999px;';
  child.style.width = '8px';
  probe.append(child);
  documentRef.body.append(probe);

  if (probe.scrollLeft > 0) {
    rtlScrollType = 'reverse';
  } else {
    probe.scrollLeft = 1;
    rtlScrollType = probe.scrollLeft === 0 ? 'negative' : 'default';
  }
  probe.remove();
  return rtlScrollType;
};

const getLogicalScrollLeft = (scrollLeft, maximum, type) => {
  if (type === 'negative') return -scrollLeft;
  if (type === 'reverse') return maximum - scrollLeft;
  return scrollLeft;
};

const getPhysicalScrollLeft = (logical, maximum, type) => {
  if (type === 'negative') return logical === 0 ? 0 : -logical;
  if (type === 'reverse') return maximum - logical;
  return logical;
};

const CarouselElement = globalThis.HTMLElement ?? class {};

export class RilavereCarousel extends CarouselElement {
  constructor() {
    super();
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerEnd = this.handlePointerEnd.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleNativeDragStart = this.handleNativeDragStart.bind(this);
    this.handleRtlButtonClick = this.handleRtlButtonClick.bind(this);
    this.updateRtlControls = this.updateRtlControls.bind(this);
  }

  connectedCallback() {
    if (this.bound) return;

    this.track = this.querySelector('[data-rilavere-carousel-track]');
    if (!this.track) return;

    this.bound = true;
    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.track.addEventListener('pointermove', this.handlePointerMove);
    this.track.addEventListener('pointerup', this.handlePointerEnd);
    this.track.addEventListener('pointercancel', this.handlePointerEnd);
    this.track.addEventListener('click', this.handleClick, true);
    this.track.addEventListener('dragstart', this.handleNativeDragStart);
    this.previousButton = this.querySelector('[data-rilavere-carousel-prev]');
    this.nextButton = this.querySelector('[data-rilavere-carousel-next]');
    this.isRtl = getDirection(this.track).toLowerCase() === 'rtl';

    if (this.isRtl && this.previousButton && this.nextButton) {
      this.rtlScrollType = getRtlScrollType(this.ownerDocument);
      this.previousButton.addEventListener('click', this.handleRtlButtonClick, true);
      this.nextButton.addEventListener('click', this.handleRtlButtonClick, true);
      this.track.addEventListener('scroll', this.updateRtlControls);
      this.updateRtlControls();
    }
  }

  disconnectedCallback() {
    if (!this.bound) return;

    this.track.removeEventListener('pointerdown', this.handlePointerDown);
    this.track.removeEventListener('pointermove', this.handlePointerMove);
    this.track.removeEventListener('pointerup', this.handlePointerEnd);
    this.track.removeEventListener('pointercancel', this.handlePointerEnd);
    this.track.removeEventListener('click', this.handleClick, true);
    this.track.removeEventListener('dragstart', this.handleNativeDragStart);
    this.previousButton?.removeEventListener('click', this.handleRtlButtonClick, true);
    this.nextButton?.removeEventListener('click', this.handleRtlButtonClick, true);
    this.track.removeEventListener('scroll', this.updateRtlControls);
    this.releasePointer();
    this.resetDrag();
    clearTimeout(this.suppressionTimer);
    this.suppressNextClick = false;
    this.maxDragDistance = 0;
    this.bound = false;
    this.track = null;
    this.previousButton = null;
    this.nextButton = null;
  }

  handlePointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (getDirection(this.track).toLowerCase() === 'rtl') return;

    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.distance = 0;
    this.maxDragDistance = 0;
    this.startScrollLeft = this.track.scrollLeft;
    this.maximumScrollLeft = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    this.dragging = false;
  }

  handlePointerMove(event) {
    if (event.pointerId !== this.pointerId) return;

    this.distance = event.clientX - this.startX;
    this.maxDragDistance = updateMaxDragDistance(this.maxDragDistance, this.distance);
    if (!this.dragging && !shouldSuppressClick(this.maxDragDistance)) return;

    if (!this.dragging) {
      this.dragging = true;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture?.(event.pointerId);
    }

    this.track.scrollLeft = clampTarget(this.startScrollLeft - this.distance, this.maximumScrollLeft);
    event.preventDefault();
  }

  handlePointerEnd(event) {
    if (event.pointerId !== this.pointerId) return;

    const suppressClick = this.dragging && shouldSuppressClick(this.maxDragDistance);
    this.releasePointer();
    this.resetDrag();

    if (suppressClick) {
      this.suppressNextClick = true;
      clearTimeout(this.suppressionTimer);
      this.suppressionTimer = setTimeout(() => {
        this.suppressNextClick = false;
      }, 0);
    }
  }

  handleClick(event) {
    if (!this.suppressNextClick) return;

    event.preventDefault();
    event.stopPropagation();
    this.suppressNextClick = false;
    this.maxDragDistance = 0;
    clearTimeout(this.suppressionTimer);
  }

  handleNativeDragStart(event) {
    event.preventDefault();
  }

  handleRtlButtonClick(event) {
    if (!this.isRtl) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const maximum = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    const items = this.track.querySelectorAll('.rilavere-selected-carousel__item');
    const step = Number(event.currentTarget.dataset.step || 1) * (
      items.length > 1
        ? getCardStep({ firstOffset: items[0].offsetLeft, secondOffset: items[1].offsetLeft })
        : this.track.clientWidth
    );
    const current = clampTarget(getLogicalScrollLeft(this.track.scrollLeft, maximum, this.rtlScrollType), maximum);
    const direction = event.currentTarget.name === 'next' ? 1 : -1;
    const target = clampTarget(current + direction * step, maximum);
    const left = getPhysicalScrollLeft(target, maximum, this.rtlScrollType);

    if (this.track.scrollTo) this.track.scrollTo({ left, behavior: 'auto' });
    else this.track.scrollLeft = left;
    this.updateRtlControls();
  }

  updateRtlControls() {
    if (!this.isRtl || !this.previousButton || !this.nextButton) return;

    const maximum = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    const current = clampTarget(getLogicalScrollLeft(this.track.scrollLeft, maximum, this.rtlScrollType), maximum);
    this.previousButton.toggleAttribute('disabled', current <= rtlBoundaryTolerance);
    this.nextButton.toggleAttribute('disabled', current >= maximum - rtlBoundaryTolerance);
  }

  releasePointer() {
    if (this.pointerId === undefined || !this.track) return;
    if (this.track.hasPointerCapture?.(this.pointerId)) this.track.releasePointerCapture(this.pointerId);
  }

  resetDrag() {
    this.track?.classList.remove('is-dragging');
    this.dragging = false;
    this.pointerId = undefined;
    this.distance = 0;
  }
}

if (globalThis.customElements && !globalThis.customElements.get('rilavere-carousel')) {
  globalThis.customElements.define('rilavere-carousel', RilavereCarousel);
}
